// File Structure for Dental Chart System
// src/components/dental/

// 1. DentalChartingPage.js - Main container component
import React, { useState } from 'react';
import { Card, Row, Col, Button, Form, Space, Typography, Tabs } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PrinterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import DentalChart from './DentalChart';
import TreatmentModal from './TreatmentModal';
import TreatmentHistory from '../TreatmentHistory';
import XrayManager from './XrayManager';
import ClinicalNotes from './ClinicalNotes';
import PatientInfo from './PatientInfo';

const { Title } = Typography;
const { TabPane } = Tabs;

const DentalChartingPage = () => {
  const navigate = useNavigate();
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [chartData, setChartData] = useState({});
  const [treatmentModal, setTreatmentModal] = useState(false);
  const [patientForm] = Form.useForm();
  const [treatmentHistory, setTreatmentHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('chart');
  const [xrayImages, setXrayImages] = useState([]);

  const handleToothClick = (toothNumber) => {
    setSelectedTooth(toothNumber);
    setTreatmentModal(true);
  };

  const handleSaveTreatment = (toothNumber, treatmentData) => {
    const newChartData = { ...chartData };
    
    if (!newChartData[toothNumber]) {
      newChartData[toothNumber] = {};
    }
    
    // Update chart data based on treatment type
    switch (treatmentData.treatmentType) {
      case 'filling':
        newChartData[toothNumber].filled = true;
        break;
      case 'extraction':
        newChartData[toothNumber].extracted = true;
        break;
      case 'crown':
        newChartData[toothNumber].crown = true;
        break;
      case 'rootCanal':
        newChartData[toothNumber].rootCanal = true;
        break;
      default:
        break;
    }
    
    if (treatmentData.condition === 'decay') {
      newChartData[toothNumber].decay = true;
    }
    
    if (treatmentData.surface) {
      newChartData[toothNumber].surface = treatmentData.surface;
    }
    
    setChartData(newChartData);
    
    const newTreatment = {
      id: Date.now(),
      toothNumber,
      date: new Date().toLocaleDateString(),
      ...treatmentData
    };
    
    setTreatmentHistory([...treatmentHistory, newTreatment]);
    setTreatmentModal(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSave = () => {
    // Implementation for saving chart data
    console.log('Saving chart data...');
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Card style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/')}
              >
                Back to Dashboard
              </Button>
              <Title level={2} style={{ margin: 0 }}>Professional Dental Charting</Title>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<SaveOutlined />} type="primary" onClick={handleSave}>
                Save Chart
              </Button>
              <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                Print Chart
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Patient Information */}
      <PatientInfo form={patientForm} />

      {/* Main Content Tabs */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="🦷 Dental Chart" key="chart">
            <DentalChart 
              onToothClick={handleToothClick}
              selectedTooth={selectedTooth}
              chartData={chartData}
              setChartData={setChartData}
            />
          </TabPane>

          <TabPane tab={`📋 Treatment History (${treatmentHistory.length})`} key="history">
            <TreatmentHistory 
              treatmentHistory={treatmentHistory}
              setTreatmentHistory={setTreatmentHistory}
            />
          </TabPane>

          <TabPane tab={`📸 X-rays (${xrayImages.length})`} key="xrays">
            <XrayManager 
              xrayImages={xrayImages}
              setXrayImages={setXrayImages}
            />
          </TabPane>

          <TabPane tab="📝 Clinical Notes" key="notes">
            <ClinicalNotes />
          </TabPane>
        </Tabs>
      </Card>

      {/* Treatment Modal */}
      <TreatmentModal
        visible={treatmentModal}
        onCancel={() => setTreatmentModal(false)}
        onSave={handleSaveTreatment}
        selectedTooth={selectedTooth}
        chartData={chartData}
      />
    </div>
  );
};

export default DentalChartingPage;